import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "../lib/api.js";
import type { CollectionItem } from "../lib/types.js";
import { Icon, type IconName } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { Modal } from "../components/Modal.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { useContextMenu, type MenuItem } from "../components/ContextMenu.js";
import { useModelPicker } from "../ai/ModelPicker.js";
import { CharacterAvatar } from "../components/CharacterAvatar.js";
import type { ModelRef } from "../lib/types.js";
import { DetailEditor, type CollectionRefs } from "./CollectionPanel.js";
import { COLLECTION_CONFIGS } from "./config.js";

interface Props {
  projectId: string;
  refs: CollectionRefs;
  onOpenScene: (sceneId: string) => void;
  /** AI organize runs in the workspace so it survives navigation. */
  organizing?: boolean;
  reloadKey?: number;
  onOrganize?: (model: ModelRef) => void;
}

type Layout = "portrait" | "circle" | "compact";
const LAYOUTS: { key: Layout; label: string }[] = [
  { key: "portrait", label: "Large photos" },
  { key: "circle", label: "Circular" },
  { key: "compact", label: "Compact" }
];
const SECTION_ICONS: IconName[] = ["users", "crown", "star", "heart", "gamepad", "briefcase", "book", "map", "sparkles", "target", "thread", "volume", "home"];

const str = (v: unknown) => (typeof v === "string" ? v : "");
const splitTags = (v: unknown) => str(v).split(/[,;]/).map((s) => s.trim()).filter(Boolean);
function hueOf(t: string): number { let h = 0; for (let i = 0; i < t.length; i++) h = (h * 31 + t.charCodeAt(i)) % 360; return h; }
function initials(name: string): string { const p = name.trim().split(/\s+/).filter(Boolean); return p.length ? (p[0]![0]! + (p[1]?.[0] ?? "")).toUpperCase() : "?"; }

interface GroupDraft { id: string; name: string; description: string; icon: IconName; layout: Layout; }

/**
 * Characters gallery — portrait-first, sectioned view. Sections (character groups)
 * are user-defined: renamable, with a chosen icon and a per-section card layout
 * (large photos / circular / compact). "Auto-organize" uses AI to propose sections
 * and sort the cast. "View profile" opens the full character editor (reused).
 */
export function CharactersPanel({ projectId, refs, onOpenScene, organizing = false, reloadKey = 0, onOrganize }: Props) {
  const toast = useToast();
  const { pickModel } = useModelPicker();
  const config = COLLECTION_CONFIGS.characters!;
  const { open: openMenu, menu } = useContextMenu();
  const [chars, setChars] = useState<CollectionItem[] | null>(null);
  const [groups, setGroups] = useState<CollectionItem[]>([]);
  const [query, setQuery] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [groupDraft, setGroupDraft] = useState<GroupDraft | null>(null);
  const [deleteGroupItem, setDeleteGroupItem] = useState<CollectionItem | null>(null);
  const [deleteChar, setDeleteChar] = useState<CollectionItem | null>(null);
  const [confirmOrganize, setConfirmOrganize] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropHint, setDropHint] = useState<string | null>(null);
  const [sectionDrop, setSectionDrop] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      const [c, g] = await Promise.all([
        api.listCollection(projectId, "characters"),
        api.listCollection(projectId, "character-groups")
      ]);
      setChars(c.items);
      setGroups(g.items);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load characters");
    }
  }, [projectId, toast]);
  useEffect(() => { void load(); }, [load]);
  // Reload after a workspace-run AI organize completes.
  useEffect(() => { if (reloadKey > 0) void load(); }, [reloadKey, load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (chars ?? []).filter((c) => !q || `${str(c.name)} ${str(c.tags)} ${str(c.occupation)} ${str(c.personality)}`.toLowerCase().includes(q));
  }, [chars, query]);

  const groupIds = useMemo(() => new Set(groups.map((g) => g.id)), [groups]);
  // Manual order (position) first, then name for anything unpositioned.
  const orderSort = (a: CollectionItem, b: CollectionItem) => {
    const pa = typeof a.position === "number" ? a.position : Number.POSITIVE_INFINITY;
    const pb = typeof b.position === "number" ? b.position : Number.POSITIVE_INFINITY;
    return pa - pb || str(a.name).localeCompare(str(b.name));
  };
  const byGroup = (id: string) => filtered.filter((c) => str(c.groupId) === id).sort(orderSort);
  const ungrouped = filtered.filter((c) => !groupIds.has(str(c.groupId))).sort(orderSort);
  const editing = useMemo(() => (chars ?? []).find((c) => c.id === editingId) ?? null, [chars, editingId]);

  const addCharacter = async (groupId: string | null) => {
    try {
      const { item } = await api.createCollectionItem(projectId, "characters", { name: "New Character", ...(groupId ? { groupId } : {}) });
      await load();
      setEditingId(item.id);
    } catch (err) { toast.error(err instanceof Error ? err.message : "Couldn't add character"); }
  };
  const addSection = async () => {
    try {
      const { item } = await api.createCollectionItem(projectId, "character-groups", { name: "New Section", layout: "portrait", icon: "users" });
      await load();
      setGroupDraft({ id: item.id, name: str(item.name), description: str(item.description), icon: "users", layout: "portrait" });
    } catch (err) { toast.error(err instanceof Error ? err.message : "Couldn't add section"); }
  };
  const saveGroup = async () => {
    if (!groupDraft) return;
    const g = groupDraft;
    setGroupDraft(null);
    try { await api.updateCollectionItem("character-groups", g.id, { name: g.name.trim() || "Untitled", description: g.description.trim() || null, icon: g.icon, layout: g.layout }); await load(); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Couldn't save section"); }
  };
  const moveTo = async (charId: string, groupId: string | null) => {
    setChars((prev) => prev ? prev.map((c) => c.id === charId ? { ...c, groupId } : c) : prev);
    try { await api.updateCollectionItem("characters", charId, { groupId }); }
    catch { await load(); toast.error("Couldn't move character"); }
  };
  const doDeleteGroup = async () => {
    if (!deleteGroupItem) return;
    const id = deleteGroupItem.id;
    setDeleteGroupItem(null);
    try { await api.deleteCollectionItem("character-groups", id); await load(); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Couldn't delete section"); }
  };
  const doDeleteChar = async () => {
    if (!deleteChar) return;
    const id = deleteChar.id;
    setDeleteChar(null);
    if (editingId === id) setEditingId(null);
    try { await api.deleteCollectionItem("characters", id); setChars((prev) => prev ? prev.filter((c) => c.id !== id) : prev); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Delete failed"); }
  };
  const organize = async () => {
    setConfirmOrganize(false);
    const choice = await pickModel({ task: "general-chat" });
    if (!choice) return;
    onOrganize?.(choice); // runs in the workspace so you can navigate away while it works
  };

  // Reorder a section up/down among the other sections.
  const moveSection = async (id: string, dir: -1 | 1) => {
    const idx = groups.findIndex((g) => g.id === id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= groups.length) return;
    const next = [...groups];
    [next[idx], next[j]] = [next[j]!, next[idx]!];
    setGroups(next);
    try { await api.reorderCharacterGroups(projectId, next.map((g) => g.id)); }
    catch { await load(); toast.error("Couldn't reorder sections"); }
  };

  // Drag a character card onto another to reorder (and move it into the target's
  // section if different). Persists positions for the affected section.
  const dropOn = async (targetId: string) => {
    const sourceId = dragId;
    setDragId(null); setDropHint(null);
    if (!sourceId || sourceId === targetId || !chars) return;
    const target = chars.find((c) => c.id === targetId);
    if (!target) return;
    const destGroup = str(target.groupId) && groupIds.has(str(target.groupId)) ? str(target.groupId) : "";
    // Current ordered ids of the destination section (what the user sees).
    const destList = (destGroup ? byGroup(destGroup) : ungrouped).map((c) => c.id).filter((id) => id !== sourceId);
    const at = destList.indexOf(targetId);
    destList.splice(at < 0 ? destList.length : at, 0, sourceId);
    // Optimistic update: set source group + rewrite positions for the dest section.
    setChars((prev) => prev ? prev.map((c) => c.id === sourceId ? { ...c, groupId: destGroup || null } : c).map((c) => {
      const i = destList.indexOf(c.id); return i >= 0 ? { ...c, position: i } : c;
    }) : prev);
    try {
      if (str(target.groupId) !== str(chars.find((c) => c.id === sourceId)?.groupId)) {
        await api.updateCollectionItem("characters", sourceId, { groupId: destGroup || null });
      }
      await api.reorderCharacters(projectId, destList);
    } catch { await load(); toast.error("Couldn't reorder"); }
  };

  // Drop a character onto a section (header / empty area) — move it into that
  // section, appended to the end. Same-section drops are left to card reordering.
  const dropIntoSection = async (groupId: string | null) => {
    const sourceId = dragId;
    setDragId(null); setSectionDrop(null); setDropHint(null);
    if (!sourceId || !chars) return;
    const src = chars.find((c) => c.id === sourceId);
    if (!src) return;
    const current = str(src.groupId) && groupIds.has(str(src.groupId)) ? str(src.groupId) : "";
    const dest = groupId ?? "";
    if (current === dest) return;
    const destList = (dest ? byGroup(dest) : ungrouped).map((c) => c.id).filter((id) => id !== sourceId);
    destList.push(sourceId);
    setChars((prev) => prev ? prev.map((c) => c.id === sourceId ? { ...c, groupId: dest || null } : c).map((c) => {
      const i = destList.indexOf(c.id); return i >= 0 ? { ...c, position: i } : c;
    }) : prev);
    try {
      await api.updateCollectionItem("characters", sourceId, { groupId: dest || null });
      await api.reorderCharacters(projectId, destList);
    } catch { await load(); toast.error("Couldn't move character"); }
  };

  const toggleSelect = (id: string) => setSelected((prev) => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });
  // Select (or, if all already selected, deselect) every character in a section.
  const selectAllIn = (items: CollectionItem[]) => setSelected((prev) => {
    const ids = items.map((i) => i.id);
    const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
    const next = new Set(prev);
    for (const id of ids) allSelected ? next.delete(id) : next.add(id);
    return next;
  });

  // Move all selected characters into a section (append), then clear the selection.
  const moveSelectedTo = async (groupId: string | null) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setChars((prev) => prev ? prev.map((c) => ids.includes(c.id) ? { ...c, groupId } : c) : prev);
    setSelected(new Set());
    try {
      for (const id of ids) await api.updateCollectionItem("characters", id, { groupId });
      await load();
    } catch { await load(); toast.error("Couldn't move selection"); }
  };

  const cardMenu = (c: CollectionItem): MenuItem[] => [
    { label: "View profile", icon: "edit", onClick: () => setEditingId(c.id) },
    ...groups.filter((g) => g.id !== str(c.groupId)).map((g) => ({ label: `Move to ${str(g.name)}`, icon: (str(g.icon) as IconName) || "users", onClick: () => void moveTo(c.id, g.id) })),
    ...(str(c.groupId) ? [{ label: "Move to Unassigned", icon: "minus" as const, onClick: () => void moveTo(c.id, null) }] : []),
    { label: "Delete character", icon: "trash", danger: true, onClick: () => setDeleteChar(c) }
  ];

  const renderCard = (c: CollectionItem, layout: Layout) => {
    const tags = splitTags(c.tags);
    const name = str(c.name) || "Unnamed";
    const img = c.imageData as string | null | undefined;
    const blurb = str(c.personality) || str(c.background) || str(c.occupation);
    const tagChips = (justify: "center" | "flex-start") => tags.length > 0 ? (
      <div className="charcard-tags" style={{ justifyContent: justify }}>
        {tags.slice(0, 3).map((t) => <span key={t} className="charcard-tag" style={{ background: `hsl(${hueOf(t)} 45% 26%)`, color: `hsl(${hueOf(t)} 80% 85%)` }}>{t}</span>)}
      </div>
    ) : null;
    const isSel = selected.has(c.id);
    const menuBtn = (
      <button className="charcard-menu" title="Options" onClick={(e) => { e.stopPropagation(); openMenu(e, cardMenu(c)); }}><Icon name="dots" size={15} /></button>
    );
    const selectBtn = (
      <button className={`charcard-select ${isSel ? "on" : ""}`} title={isSel ? "Deselect" : "Select"} onClick={(e) => { e.stopPropagation(); toggleSelect(c.id); }}>
        {isSel && <Icon name="check" size={13} />}
      </button>
    );

    let inner: ReactNode;
    if (layout === "compact") {
      inner = (
        <>
          {selectBtn}{menuBtn}
          <button className="charcard-compact-body" onClick={() => setEditingId(c.id)}>
            <CharacterAvatar name={name} imageUrl={img} size="md" ring />
            <div style={{ minWidth: 0 }}>
              <div className="charcard-name" style={{ fontSize: 13 }}>{name}</div>
              {tags[0] && <span className="charcard-tag" style={{ background: `hsl(${hueOf(tags[0])} 45% 26%)`, color: `hsl(${hueOf(tags[0])} 80% 85%)` }}>{tags[0]}</span>}
              {str(c.occupation) && <div className="charcard-occ" style={{ marginTop: 3 }}>{str(c.occupation)}</div>}
            </div>
          </button>
        </>
      );
    } else if (layout === "portrait") {
      inner = (
        <>
          {selectBtn}{menuBtn}
          <button className="charcard-portrait-body" onClick={() => setEditingId(c.id)}>
            {img ? (
              <div className="charphoto"><img src={img} alt={name} draggable={false} /></div>
            ) : (
              <div className="charphoto charphoto-initials" style={{ background: `hsl(${hueOf(name)} 40% 28%)`, color: `hsl(${hueOf(name)} 72% 85%)` }}>{initials(name)}</div>
            )}
            <div className="charcard-info">
              <div className="charcard-name">{name}</div>
              {str(c.occupation) && <div className="charcard-occ">{str(c.occupation)}</div>}
              {tagChips("flex-start")}
              {blurb && <div className="charcard-blurb">{blurb.slice(0, 150)}{blurb.length > 150 ? "…" : ""}</div>}
            </div>
          </button>
          <button className="charcard-view" onClick={() => setEditingId(c.id)}>View Profile <Icon name="chevronRight" size={13} /></button>
        </>
      );
    } else {
      inner = (
        <>
          {selectBtn}{menuBtn}
          <button className="charcard-body" onClick={() => setEditingId(c.id)}>
            <CharacterAvatar name={name} imageUrl={img} size="lg" ring />
            <div className="charcard-name">{name}</div>
            {str(c.occupation) && <div className="charcard-occ">{str(c.occupation)}</div>}
            {tagChips("center")}
            {blurb && <div className="charcard-blurb">{blurb.slice(0, 150)}{blurb.length > 150 ? "…" : ""}</div>}
          </button>
          <button className="charcard-view" onClick={() => setEditingId(c.id)}>View Profile <Icon name="chevronRight" size={13} /></button>
        </>
      );
    }

    return (
      <div
        key={c.id}
        className={`charcard${layout === "compact" ? " charcard-compact" : ""}${dropHint === c.id ? " charcard-drop" : ""}${dragId === c.id ? " charcard-dragging" : ""}${isSel ? " charcard-selected" : ""}`}
        draggable
        onDragStart={(e) => { setDragId(c.id); e.dataTransfer.effectAllowed = "move"; }}
        onDragEnd={() => { setDragId(null); setDropHint(null); }}
        onDragOver={(e) => { if (dragId && dragId !== c.id) { e.preventDefault(); e.stopPropagation(); setDropHint(c.id); setSectionDrop(null); } }}
        onDragLeave={() => setDropHint((h) => (h === c.id ? null : h))}
        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); void dropOn(c.id); }}
      >
        {inner}
      </div>
    );
  };

  const section = (opts: { id: string | null; name: string; description?: string; icon: IconName; layout: Layout; items: CollectionItem[] }) => {
    const key = opts.id ?? "unassigned";
    return (
    <section
      key={key}
      className={`charsection${sectionDrop === key ? " charsection-drop" : ""}`}
      onDragOver={(e) => { if (dragId) { e.preventDefault(); setSectionDrop(key); } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setSectionDrop((h) => (h === key ? null : h)); }}
      onDrop={(e) => { e.preventDefault(); void dropIntoSection(opts.id); }}
    >
      <div className="charsection-head">
        <Icon name={opts.icon} size={17} style={{ color: "var(--accent)" }} />
        <div className="grow" style={{ minWidth: 0 }}>
          <div className="charsection-title">{opts.name}</div>
          {opts.description && <div className="charsection-sub">{opts.description}</div>}
        </div>
        <span className="charsection-count">{opts.items.length} character{opts.items.length === 1 ? "" : "s"}</span>
        {opts.items.length > 0 && (
          <button className="btn btn-icon btn-ghost btn-sm" title="Select all in this section" onClick={() => selectAllIn(opts.items)}>
            <Icon name={opts.items.every((i) => selected.has(i.id)) ? "check" : "plus"} size={14} />
          </button>
        )}
        {selected.size > 0 && (
          <button className="btn btn-sm btn-primary" title={`Move ${selected.size} selected here`} onClick={() => void moveSelectedTo(opts.id)}>
            <Icon name="download" size={13} /> Move {selected.size} here
          </button>
        )}
        {opts.id && !query && (() => {
          const idx = groups.findIndex((g) => g.id === opts.id);
          return (
            <span className="charsection-reorder">
              <button className="btn btn-icon btn-ghost btn-sm" title="Move section up" disabled={idx <= 0} onClick={() => void moveSection(opts.id!, -1)}><Icon name="chevronUp" size={14} /></button>
              <button className="btn btn-icon btn-ghost btn-sm" title="Move section down" disabled={idx >= groups.length - 1} onClick={() => void moveSection(opts.id!, 1)}><Icon name="chevronDown" size={14} /></button>
            </span>
          );
        })()}
        <button className="btn btn-icon btn-ghost btn-sm" title="Add a character to this section" onClick={() => void addCharacter(opts.id)}><Icon name="plus" size={14} /></button>
        {opts.id && (
          <button className="btn btn-icon btn-ghost btn-sm" title="Section options" onClick={(e) => openMenu(e, [
            { label: "Edit section", icon: "edit", onClick: () => setGroupDraft({ id: opts.id!, name: opts.name, description: opts.description ?? "", icon: opts.icon, layout: opts.layout }) },
            { label: "Delete section", icon: "trash", danger: true, onClick: () => setDeleteGroupItem(groups.find((g) => g.id === opts.id) ?? null) }
          ])}><Icon name="dots" size={15} /></button>
        )}
      </div>
      {opts.items.length === 0 ? (
        <p className="faint" style={{ fontSize: 12, padding: "2px 2px 6px" }}>No characters here yet.</p>
      ) : (
        <div className={`charcard-grid charcard-grid--${opts.layout}`}>{opts.items.map((c) => renderCard(c, opts.layout))}</div>
      )}
    </section>
    );
  };

  return (
    <div className={`chars-page${selected.size > 0 ? " selecting" : ""}`}>
      <header className="chars-hero">
        <div>
          <h1 className="chars-h1">Characters</h1>
          <p className="chars-tagline">The people who bring your story to life.</p>
        </div>
        <div className="row gap-2" style={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
          <input className="input" placeholder="Search characters…" value={query} onChange={(e) => setQuery(e.target.value)} style={{ maxWidth: 220 }} />
          <button className="btn btn-sm btn-ghost" onClick={() => setConfirmOrganize(true)} disabled={organizing} title="Let AI create sections and sort characters">
            <Icon name={organizing ? "clock" : "sparkles"} size={14} className={organizing ? "spin" : ""} /> Auto-organize
          </button>
          <button className="btn btn-sm btn-ghost" onClick={() => void addSection()}><Icon name="plus" size={13} /> Section</button>
          <button className="btn btn-sm btn-primary" onClick={() => void addCharacter(null)}><Icon name="plus" size={14} /> Add Character</button>
        </div>
      </header>

      {selected.size > 0 && (
        <div className="chars-selectbar">
          <Icon name="check" size={14} style={{ color: "var(--accent)" }} />
          <span><strong>{selected.size}</strong> selected</span>
          <span className="faint" style={{ fontSize: 12 }}>— use “Move here” on a section, or drag.</span>
          <span className="spacer" style={{ flex: 1 }} />
          <button className="btn btn-sm btn-ghost" onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}

      {organizing && (
        <div className="chars-organizing">
          <div className="chars-organizing-row">
            <Icon name="sparkles" size={14} className="spin" style={{ color: "var(--accent)" }} />
            <span>AI is organizing your characters into sections… you can keep working — this finishes in the background.</span>
          </div>
          <div className="chars-progress"><div className="chars-progress-bar" /></div>
        </div>
      )}

      {chars === null ? (
        <div className="insights-loading"><Icon name="sparkles" size={22} className="spin" style={{ color: "var(--accent)" }} /></div>
      ) : (chars.length === 0 && groups.length === 0) ? (
        <section className="card insight-card" style={{ margin: 20 }}><p className="muted insight-empty">No characters yet. Add one, create a section, or use Auto-organize once you have a cast.</p></section>
      ) : (
        <div className="chars-sections">
          {groups.map((g) => section({ id: g.id, name: str(g.name), description: str(g.description), icon: (str(g.icon) as IconName) || "users", layout: (str(g.layout) as Layout) || "portrait", items: byGroup(g.id) }))}
          {ungrouped.length > 0 && section({ id: null, name: groups.length ? "Unassigned" : "All characters", icon: "users", layout: "portrait", items: ungrouped })}
        </div>
      )}

      {editing && (
        <Modal title={str(editing.name) || "Character"} width={720} onClose={() => setEditingId(null)}>
          <div className="char-editor-modal">
            <DetailEditor
              item={editing}
              config={config}
              refs={refs}
              onChanged={(updated) => setChars((prev) => prev ? prev.map((c) => c.id === updated.id ? updated : c) : prev)}
              onDelete={() => setDeleteChar(editing)}
              onOpenScene={onOpenScene}
            />
          </div>
        </Modal>
      )}

      {groupDraft && (
        <Modal title="Section" width={460} onClose={() => void saveGroup()} footer={<button className="btn btn-primary" onClick={() => void saveGroup()}>Done</button>}>
          <label className="field"><span className="label">Name</span>
            <input className="input" autoFocus value={groupDraft.name} onChange={(e) => setGroupDraft((g) => g && { ...g, name: e.target.value })} placeholder="Main Characters" />
          </label>
          <label className="field" style={{ marginTop: 10 }}><span className="label">Description</span>
            <input className="input" value={groupDraft.description} onChange={(e) => setGroupDraft((g) => g && { ...g, description: e.target.value })} placeholder="The heart of the story." />
          </label>
          <div className="field" style={{ marginTop: 12 }}><span className="label">Icon</span>
            <div className="icon-picker">
              {SECTION_ICONS.map((ic) => (
                <button key={ic} className={`icon-pick ${groupDraft.icon === ic ? "active" : ""}`} onClick={() => setGroupDraft((g) => g && { ...g, icon: ic })} title={ic}>
                  <Icon name={ic} size={17} />
                </button>
              ))}
            </div>
          </div>
          <div className="field" style={{ marginTop: 12 }}><span className="label">Card style</span>
            <div className="row gap-2">
              {LAYOUTS.map((l) => (
                <button key={l.key} className={`btn btn-sm ${groupDraft.layout === l.key ? "btn-primary" : "btn-ghost"}`} onClick={() => setGroupDraft((g) => g && { ...g, layout: l.key })}>{l.label}</button>
              ))}
            </div>
          </div>
        </Modal>
      )}

      {confirmOrganize && (
        <ConfirmDialog
          title="Auto-organize characters"
          message="Let AI create sections and sort your characters into them? This reassigns characters' sections — you can move them back afterward."
          confirmLabel="Organize"
          onConfirm={() => void organize()}
          onCancel={() => setConfirmOrganize(false)}
        />
      )}
      {deleteGroupItem && (
        <ConfirmDialog title="Delete section" message={`Delete the section “${str(deleteGroupItem.name)}”? Its characters move to Unassigned (they are not deleted).`} confirmLabel="Delete section" danger onConfirm={() => void doDeleteGroup()} onCancel={() => setDeleteGroupItem(null)} />
      )}
      {deleteChar && (
        <ConfirmDialog title="Delete character" message={`Delete “${str(deleteChar.name) || "Unnamed"}”? This can't be undone.`} confirmLabel="Delete" danger onConfirm={() => void doDeleteChar()} onCancel={() => setDeleteChar(null)} />
      )}
      {menu}
    </div>
  );
}
